import { IconKey } from './icon';
import { MenuItem, MenuItemType } from './menu';
import { PortalBlockEmbedType, PortalBlockPlace } from './portal-block';
import { FlexibleBlockTemplate } from './portal-block-flexible';
import { TileCategory, TileType } from './tiles';

export enum ScreenType {
  MAIN = 'main',
  NIMBUS_PAGE = 'nimbus-page',
  FUSEBASE_PAGE = 'fusebase-page',
  CHOOSE_BLOCK = 'choose-block',
  BLOCK_LINK = 'block-link',
  BLOCK_EMBED = 'block-embed',
  BLOCK_INFO = 'block-info',
  BLOCK_TASKS = 'block-tasks',
  BLOCK_CHAT = 'block-chat',
  BLOCK_HELP = 'block-help',
  BLOCK_REQUEST = 'block-request',
  BLOCK_MEETINGS = 'block-meetings',
  BLOCK_FUSEBASE = 'block-fusebase',
  BLOCK_FLEXIBLE = 'block-flexible',
  BLOCK_FLEXIBLE_INITIAL = 'block-flexible-initial',
  ADD_IMAGE = 'add-image',
  ADD_ITEM = 'add-item',
  ADD_LINK = 'add-link',
  ADD_LOGO = 'add-logo',
  ADD_PROCESS = 'add-process',
  ADD_TEMPLATE = 'add-template',
  THEME = 'theme',
  THEME_DETAILED = 'theme_detailed',
  CONFIRM = 'confirm',
  SECTION = 'section',
  FILES_DASHBOARD = 'files-dashboard',
  CONTENT = 'content',
  FILES_BUCKET = 'files-bucket',
  FILES_RECENT = 'files-recent',
  HERO = 'hero',
  HERO_VIEW = 'hero-view',
  TOPBAR = 'topbar',
  TOPBAR_VIEW = 'topbar-view',
  TASKS_DASHBOARD = 'tasks-dashboard',
  CHATS_DASHBOARD = 'chats-dashboard',
  EMBEDS = 'embeds',
  PROJECTION = 'projection',
  SIDEBAR = 'sidebar',
  FOOTER = 'footer',
}

export enum UrlValidationType {
  EXTERNAL_LINK = 'external-link',
  INTERNAL_LINK = 'internal-link',
}
interface ScreenPayloadBase {
  buttonDisabled?: boolean;
  collectId?: string;
}

interface ScreenPayloadBlockBase extends ScreenPayloadBase {
  place: PortalBlockPlace;
  editItemId?: string;
}

interface ScreenItemBase {
  type: ScreenType;
}

export interface ScreenItemMain extends ScreenItemBase {
  type: ScreenType.MAIN;
}

export interface ScreenItemEmbeds extends ScreenItemBase {
  type: ScreenType.EMBEDS;
}

interface ScreenPayloadEmbed extends ScreenPayloadBlockBase {
  embedType: PortalBlockEmbedType;
}

interface ScreenPayloadNimbusPage extends ScreenPayloadBlockBase {
  multiSelect?: boolean;
  maxItemsSelect?: number;
  onConfirm?: (items: MenuItem[]) => void;
  disabledTypes?: MenuItemType[];
  disabledSelectRootFolder?: boolean;
}

interface ScreenPayloadChooseBlock extends ScreenPayloadBase {
  category?: TileCategory;
  type?: string;
  positionIndex?: number;
}

export interface ScreenItemBlockEmbed extends ScreenItemBase {
  type: ScreenType.BLOCK_EMBED;
  payload: ScreenPayloadEmbed;
}

// interface ScreenNavigationPayload extends ScreenPayloadBase {
//   expandedMenuTypes?: MenuType[];
//   initScrollToId?: string;
//   noteTempale?: PortalNoteTemplate;
// }

export interface ScreenItemChooseBlock extends ScreenItemBase {
  type: ScreenType.CHOOSE_BLOCK;
  payload?: ScreenPayloadChooseBlock;
}
export interface ScreenItemTheme extends ScreenItemBase {
  type: ScreenType.THEME;
}

export interface ScreenItemThemeDetailed extends ScreenItemBase {
  type: ScreenType.THEME_DETAILED;
  payload: ScreenPayloadBase;
}
export interface ScreenItemNimbusPage extends ScreenItemBase {
  type: ScreenType.NIMBUS_PAGE;
  payload?: ScreenPayloadNimbusPage;
}

export interface ScreenItemFuseBasePage extends ScreenItemBase {
  payload?: ScreenPayloadFuseBasePage;
  type: ScreenType.FUSEBASE_PAGE;
}
type ScreenPayloadFuseBasePage = ScreenPayloadBlockBase;

export interface ScreenItemFilesBucket extends ScreenItemBase {
  payload: ScreenPayloadFilesBucket;
  type: ScreenType.FILES_BUCKET;
}

type ScreenPayloadFilesBucket = ScreenPayloadBlockBase;

interface ScreenPayloadInfo extends ScreenPayloadBlockBase {
  nimbusPageItems?: MenuItem[];
  linkUrl?: string;
  image?: string | null;
}
export interface ScreenItemBlockInfo extends ScreenItemBase {
  type: ScreenType.BLOCK_INFO;
  payload: ScreenPayloadInfo;
}

type ScreenPayloadTasks = ScreenPayloadBlockBase;
export interface ScreenItemBlockTasks extends ScreenItemBase {
  type: ScreenType.BLOCK_TASKS;
  payload: ScreenPayloadTasks;
}

type ScreenPayloadChat = ScreenPayloadBlockBase;
export interface ScreenItemBlockChat extends ScreenItemBase {
  type: ScreenType.BLOCK_CHAT;
  payload: ScreenPayloadChat;
}

interface ScreenPayloadBlockLink extends ScreenPayloadBlockBase {
  urlValidationType?: UrlValidationType;
  title?: string;
  url?: string;
  openInNewTab?: boolean;
  visible?: boolean;
  customScreenTitle?: string;
  newTabHidden?: boolean;
  urlHidden?: boolean;
  visibilityHidden?: boolean;
  onConfirm?: (title?: string, url?: string, openNewTab?: boolean, visible?: boolean) => void;
}
export interface ScreenItemBlockLink extends ScreenItemBase {
  type: ScreenType.BLOCK_LINK;
  payload: ScreenPayloadBlockLink;
}

interface ScreenPayloadBlockAddItem extends ScreenPayloadBase {
  category?: TileCategory;
  disabledTypes?: MenuItemType[];
  excludeTileTypes?: TileType[];
  type?: string;
}
export interface ScreenItemBlockAddItem extends ScreenItemBase {
  type: ScreenType.ADD_ITEM;
  payload?: ScreenPayloadBlockAddItem;
}

interface ScreenPayloadAddImage extends ScreenPayloadBase {
  image?: string;
  onConfirm?: (value?: string | null) => void;
}
export interface ScreenItemAddImage extends ScreenItemBase {
  type: ScreenType.ADD_IMAGE;
  payload: ScreenPayloadAddImage;
}

type ScreenPayloadAddLink = ScreenPayloadBase & {
  onConfirm?: (url: string) => void;
};
export interface ScreenAddLink extends ScreenItemBase {
  type: ScreenType.ADD_LINK;
  payload: ScreenPayloadAddLink;
}

export type ScreenPayloadSectionItem = {
  title: string;
};

export type ScreenPayloadSectionConfirmProps = {
  title: string;
  url: string;
  items: ScreenPayloadSectionItem[];
};

export type ScreenPayloadSection = ScreenPayloadBase & {
  title?: string;
  url?: string;
  type?: string;
  sectionType?: 'section' | 'process';
  onConfirm?: (props: ScreenPayloadSectionConfirmProps) => void;
};
export interface ScreenItemSection extends ScreenItemBase {
  type: ScreenType.SECTION;
  payload: ScreenPayloadSection;
}

interface ScreenPayloadConfirm extends ScreenPayloadBase {
  title: string;
  onConfirm: () => void;
  description?: string;
  buttonText?: string;
  onBackClick?: () => void;
  id?: string;
}
export interface ScreenConfirm extends ScreenItemBase {
  type: ScreenType.CONFIRM;
  payload: ScreenPayloadConfirm;
}

export interface ScreenItemBlockFilesRecent extends ScreenItemBase {
  type: ScreenType.FILES_RECENT;
  payload: ScreenPayloadBlockBase;
}

export interface ScreenItemBlockHelp extends ScreenItemBase {
  type: ScreenType.BLOCK_HELP;
  payload: ScreenPayloadBlockBase;
}

export interface ScreenItemBlockMeetings extends ScreenItemBase {
  type: ScreenType.BLOCK_MEETINGS;
  payload: ScreenPayloadBlockBase;
}

export interface ScreenItemBlockRequest extends ScreenItemBase {
  type: ScreenType.BLOCK_REQUEST;
  payload: ScreenPayloadBlockBase;
}

export interface ScreenItemBlockFuseBase extends ScreenItemBase {
  type: ScreenType.BLOCK_FUSEBASE;
  payload: ScreenPayloadFuseBase;
}
export interface ScreenItemBlockContent extends ScreenItemBase {
  type: ScreenType.CONTENT;
  payload: ScreenPayloadContent;
}
type ScreenPayloadFuseBase = ScreenPayloadBlockBase & {
  title: string;
  noteId: string;
};
type ScreenPayloadContent = ScreenPayloadBlockBase & {
  title: string;
  noteId: string;
};
export interface ScreenItemAddLogo extends ScreenItemBase {
  type: ScreenType.ADD_LOGO;
}

export interface ScreenFileDashboard extends ScreenItemBase {
  type: ScreenType.FILES_DASHBOARD;
  payload: ScreenPayloadBlockBase;
}

export interface ScreenTasksDashboard extends ScreenItemBase {
  type: ScreenType.TASKS_DASHBOARD;
  payload: ScreenPayloadBlockBase;
}

export interface ScreenChatsDashboard extends ScreenItemBase {
  type: ScreenType.CHATS_DASHBOARD;
  payload: ScreenPayloadBlockBase;
}

export interface ScreenHero extends ScreenItemBase {
  type: ScreenType.HERO;
  payload: ScreenPayloadBase;
}
export interface ScreenHeroView extends ScreenItemBase {
  type: ScreenType.HERO_VIEW;
  payload: ScreenPayloadBase;
}
export interface ScreenTopbar extends ScreenItemBase {
  type: ScreenType.TOPBAR;
  payload: ScreenPayloadBase;
}
export interface ScreenTopbarView extends ScreenItemBase {
  type: ScreenType.TOPBAR_VIEW;
  payload: ScreenPayloadBase;
}

export interface ScreenPayloadFlexible extends ScreenPayloadBlockBase {
  template: FlexibleBlockTemplate;
  editItemId: string;
}
export interface ScreenItemFlexible extends ScreenItemBase {
  type: ScreenType.BLOCK_FLEXIBLE;
  payload: ScreenPayloadFlexible;
}
export interface ScreenFlexibleItemInitial extends ScreenItemBase {
  type: ScreenType.BLOCK_FLEXIBLE_INITIAL;
  payload: ScreenPayloadFlexible;
}
export interface ScreenItemBlockProjection extends ScreenItemBase {
  type: ScreenType.PROJECTION;
  payload: ScreenPayloadBlockBase;
}
export interface ScreenAddProcsss extends ScreenItemBase {
  type: ScreenType.ADD_PROCESS;
}

export interface ScreenTemplateItem {
  id: string;
  title: string;
  description: string;
  icon: IconKey;
}
export interface ScreenAddTemplate extends ScreenItemBase {
  type: ScreenType.ADD_TEMPLATE;
  payload: {
    items: ScreenTemplateItem[];
  };
}

export interface ScreenSidebar extends ScreenItemBase {
  type: ScreenType.SIDEBAR;
}

export interface ScreenFooter extends ScreenItemBase {
  type: ScreenType.FOOTER;
  payload: ScreenPayloadBase;
}

export type ScreenItem =
  | ScreenItemMain
  | ScreenItemBlockEmbed
  | ScreenItemChooseBlock
  | ScreenItemTheme
  | ScreenItemThemeDetailed
  | ScreenItemNimbusPage
  | ScreenItemFuseBasePage
  | ScreenItemBlockInfo
  | ScreenItemBlockTasks
  | ScreenItemBlockChat
  | ScreenItemBlockLink
  | ScreenItemAddImage
  | ScreenItemBlockAddItem
  | ScreenItemBlockFilesRecent
  | ScreenItemBlockHelp
  | ScreenItemBlockMeetings
  | ScreenItemBlockRequest
  | ScreenAddLink
  | ScreenConfirm
  | ScreenItemSection
  | ScreenItemBlockFuseBase
  | ScreenItemAddLogo
  | ScreenItemBlockContent
  | ScreenFileDashboard
  | ScreenItemFilesBucket
  | ScreenItemFlexible
  | ScreenFlexibleItemInitial
  | ScreenHero
  | ScreenHeroView
  | ScreenTopbar
  | ScreenTopbarView
  | ScreenTasksDashboard
  | ScreenChatsDashboard
  | ScreenItemEmbeds
  | ScreenItemBlockProjection
  | ScreenAddProcsss
  | ScreenAddTemplate
  | ScreenSidebar
  | ScreenFooter;

export interface BaseScreenProps {
  screen: ScreenItem;
  path: string;
  onBackClick: () => void;
}
